<template>
    <div class="lic-home">
        <!-- 인사말 -->
        <div class="welcome-section">
            <h2 class="welcome-title">
                <span v-if="userName">{{ userName }}</span>
                <span v-else>사용자</span>
                님, 반갑습니다!
            </h2>
        </div>

        <!-- 라이선스 요약 정보 (관리자만 표시) -->
        <div v-if="hasAdminPermission" class="panel">
            <div class="panel-header">
                <div class="left">
                    <strong>라이선스 요약</strong>
                </div>
            </div>

            <div v-if="state.loading" class="empty">불러오는 중…</div>

            <div v-else class="summary-info">
                <div class="summary-item">
                    <div class="summary-label">할당 가능한 라이선스</div>
                    <div class="summary-value">
                        {{ state.summary.remainingQuantity || 0 }} / {{ state.summary.totalQuantity || 0 }} BOX
                    </div>
                    <span class="link-manage" @click="goToLicenseManagement">
                        라이선스 관리
                    </span>
                </div>
                <div class="summary-item">
                    <div class="summary-label">활성 계정(최근 한달)</div>
                    <div class="summary-value">
                        {{ state.analytics?.activeUsers || 0 }} / {{ state.analytics?.assignedAccountCount || 0 }} 계정
                    </div>
                    <span class="link-manage" @click="goToAnalytics">
                        사용 분석
                    </span>
                </div>
            </div>
        </div>

        <!-- 내가 속한 팀 및 할당받은 제품 -->
        <div class="panel">
            <div class="panel-header">
                <div class="left">
                    <strong>BOX 할당 정보</strong>
                </div>
            </div>

            <div v-if="state.loadingCrews" class="empty">불러오는 중…</div>
            <div v-else-if="state.crews.length === 0" class="empty">현재 소속된 팀이 없습니다. 팀 관리자가 회원님의 계정을 팀에 추가하거나, 현재 계정으로 라이선스를 구입하시면 팀이 자동 생성됩니다.</div>
            <div v-else class="crew-assignment-section">
                <div class="gui-form">
                    <label class="form-label">팀 선택</label>
                    <select 
                        v-model="state.selectedCrewId" 
                        @change="onCrewChange"
                        class="crew-select"
                    >
                        <option :value="null">팀을 선택하세요</option>
                        <option 
                            v-for="crew in state.crews" 
                            :key="crew.id"
                            :value="crew.id"
                        >
                            {{ crew.crewName }} {{ crew.myRoleName ? `(${crew.myRoleName})` : '' }}
                        </option>
                    </select>
                </div>

                <!-- 선택된 팀의 할당받은 제품 목록 -->
                <div v-if="state.selectedCrewId && state.assignedProducts.length > 0" class="product-list">
                    <div class="product-list-header">
                        <strong>{{ state.selectedCrewName }} 에서 할당받은 제품</strong>
                    </div>
                    <div class="product-items">
                        <div 
                            v-for="product in state.assignedProducts" 
                            :key="(product.gid != null ? String(product.gid) + '-' : '') + product.productName"
                            class="product-item product-item--clickable"
                            role="button"
                            tabindex="0"
                            @click="openProductInstallDetail(product)"
                            @keyup.enter="openProductInstallDetail(product)"
                        >
                            <div class="product-name">{{ product.productName }}</div>
                            <div class="product-badges">
                                <span v-if="product.isOfflineAssignment" class="badge badge-offline">오프라인 할당</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="state.selectedCrewId && state.loadingProducts" class="empty">제품 정보 불러오는 중…</div>
                <div v-else-if="state.selectedCrewId && !state.loadingProducts" class="empty">할당받은 제품이 없습니다.</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'licenseHome',
    data() {
        return {
            state: {
                loading: false,
                loadingAnalytics: false,
                loadingCrews: false,
                loadingProducts: false,
                summary: {
                    totalQuantity: 0,
                    assignedQuantity: 0,
                    remainingQuantity: 0,
                },
                analytics: null,
                crews: [],
                selectedCrewId: null,
                selectedCrewName: '',
                assignedProducts: [],
            },
            userName: null,
            userRole: null, // 'CREW_ADMIN', 'SUB_ADMIN', 'MEMBER'
        };
    },
    computed: {
        hasAdminPermission() {
            // 팀 관리자 또는 보조 관리자인 경우 true
            return this.userRole === 'CREW_ADMIN' || this.userRole === 'SUB_ADMIN';
        },
    },
    mounted() {
        // 로그인 체크
        if (!this.isLogin()) {
            return;
        }
        
        // CSS 파일 동적 로드
        if (!document.querySelector('link[href="/ssflex/assets/css/crew-license-manager.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/ssflex/assets/css/crew-license-manager.css';
            document.head.appendChild(link);
        }
        this.loadUserInfo();
        this.loadMyCrews();
    },
    methods: {
        loadUserInfo() {
            if (!this.isLogin()) {
                return;
            }
            
            try {
                const account = this.getSessionProc();
                if (!account) {
                    this.userRole = 'MEMBER';
                    return;
                }

                // 사용자 이름 설정
                this.userName = account.userName || account.nickName || null;
                
                // 세션에서 팀 역할 정보 읽기
                this.userRole = account.crewRole || 'MEMBER';
                
                // 관리자인 경우에만 라이선스 정보 로드
                if (this.userRole === 'SUB_ADMIN' || this.userRole === 'CREW_ADMIN') {
                    this.loadLicenseSummary();
                    this.loadLicenseAnalytics();
                }
            } catch (e) {
                console.error('Error loading user info:', e);
                this.userRole = 'MEMBER';
            }
        },
        loadLicenseSummary() {
            if (!this.isLogin()) {
                return;
            }
            this.state.loading = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getLicenseSummary',
                {},
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.summary.totalQuantity = data.totalQuantity || 0;
                            this.state.summary.assignedQuantity = data.assignedQuantity || 0;
                            this.state.summary.remainingQuantity = data.remainingQuantity || 0;
                        } else {
                            this.gritterErr(data.msg || '라이선스 요약 정보 조회 실패');
                        }
                    } finally {
                        this.state.loading = false;
                    }
                }
            );
        },
        loadLicenseAnalytics() {
            if (!this.isLogin()) {
                return;
            }
            // 최근 1개월 기간 설정
            const endDate = new Date();
            const startDate = new Date();
            startDate.setMonth(startDate.getMonth() - 1);
            
            const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            this.state.loadingAnalytics = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getLicenseAnalytics',
                {
                    periodStart: formatDate(startDate),
                    periodEnd: formatDate(endDate),
                    crewId: 'all',
                },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.analytics = data.analytics || null;
                        } else {
                            this.gritterErr(data.msg || '라이선스 분석 정보 조회 실패');
                            this.state.analytics = null;
                        }
                    } catch (e) {
                        console.error('Analytics fetch error:', e);
                        this.state.analytics = null;
                    } finally {
                        this.state.loadingAnalytics = false;
                    }
                }
            );
        },
        goToLicenseManagement() {
            const targetQuery = {
                mainTab: 'user',
                subTab: 'products'
            };
            
            // 현재 경로와 비교하여 같은 경로면 강제로 업데이트
            const currentRoute = this.$route;
            const isSameRoute = currentRoute.name === 'licenseManagement' &&
                currentRoute.query?.mainTab === targetQuery.mainTab &&
                currentRoute.query?.subTab === targetQuery.subTab;
            
            if (isSameRoute) {
                // 같은 경로면 query에 타임스탬프를 추가하여 강제로 변경 감지
                this.$router.replace({
                    name: 'licenseManagement',
                    query: {
                        ...targetQuery,
                        _t: Date.now()
                    }
                });
            } else {
                this.$router.push({
                    name: 'licenseManagement',
                    query: targetQuery
                });
            }
        },
        /**
         * 제품 설치 화면의「자세히 보기」와 동일하게 설치 안내 상세(productDownDetail)로 이동
         */
        openProductInstallDetail(product) {
            const boardKey = 'product_download';
            let boardIdx = null;
            if (product && product.boardIdx != null && Number(product.boardIdx) > 0) {
                boardIdx = Number(product.boardIdx);
            }
            const gid = product && product.gid != null ? Number(product.gid) : null;

            if (!boardIdx && !gid) {
                this.gritterErr('이 제품의 설치 안내를 불러올 수 없습니다.');
                return;
            }

            if (this.$router) {
                if (boardIdx) {
                    this.$router.push({
                        name: 'productDownDetail',
                        params: {
                            boardKey,
                            idx: boardIdx
                        }
                    });
                } else {
                    this.$router.push({
                        name: 'productDownDetail',
                        params: {
                            boardKey,
                            idx: 0
                        },
                        query: { gid }
                    });
                }
            } else if (boardIdx) {
                window.location.href = `/app/board/${boardKey}/detail/${boardIdx}`;
            } else {
                window.location.href = `/app/board/${boardKey}/detail/0?gid=${encodeURIComponent(gid)}`;
            }
        },
        goToAnalytics() {
            const targetQuery = {
                mainTab: 'analytics'
            };
            
            // 현재 경로와 비교하여 같은 경로면 강제로 업데이트
            const currentRoute = this.$route;
            const isSameRoute = currentRoute.name === 'licenseManagement' &&
                currentRoute.query?.mainTab === targetQuery.mainTab;
            
            if (isSameRoute) {
                // 같은 경로면 query에 타임스탬프를 추가하여 강제로 변경 감지
                this.$router.replace({
                    name: 'licenseManagement',
                    query: {
                        ...targetQuery,
                        _t: Date.now()
                    }
                });
            } else {
                this.$router.push({
                    name: 'licenseManagement',
                    query: targetQuery
                });
            }
        },
        loadMyCrews() {
            if (!this.isLogin()) {
                return;
            }
            
            this.state.loadingCrews = true;
            const account = this.getSessionProc();
            if (!account) {
                this.state.loadingCrews = false;
                return;
            }

            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getMyCrewList',
                {
                    userId: account.coEmailId || account.email,
                },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.crews = data.list || [];
                            // 첫 번째 팀 자동 선택
                            if (this.state.crews.length > 0 && !this.state.selectedCrewId) {
                                this.state.selectedCrewId = this.state.crews[0].id;
                                this.onCrewChange();
                            }
                        } else {
                            this.gritterErr(data.msg || '팀 목록 조회 실패');
                        }
                    } catch (e) {
                        console.error('Error loading crews:', e);
                        this.gritterErr('팀 목록 조회 중 오류가 발생했습니다.');
                    } finally {
                        this.state.loadingCrews = false;
                    }
                }
            );
        },
        onCrewChange() {
            const crewId = this.state.selectedCrewId;
            if (!crewId) {
                this.state.selectedCrewName = '';
                this.state.assignedProducts = [];
                return;
            }

            const selectedCrew = this.state.crews.find(c => c.id === crewId);
            this.state.selectedCrewName = selectedCrew ? selectedCrew.crewName : '';
            this.loadMyLicenseInfo(crewId);
        },
        loadMyLicenseInfo(crewId) {
            if (!this.isLogin() || !crewId) {
                return;
            }
            
            this.state.loadingProducts = true;
            this.state.assignedProducts = [];
            
            const account = this.getSessionProc();
            if (!account) {
                this.state.loadingProducts = false;
                return;
            }

            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getMyLicenseInfo',
                {
                    userId: account.coEmailId || account.email,
                    crewId: crewId,
                },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.assignedProducts = data.licenseList || [];
                            if (data.crewName) {
                                this.state.selectedCrewName = data.crewName;
                            }
                        } else {
                            this.gritterErr(data.msg || '제품 정보 조회 실패');
                        }
                    } catch (e) {
                        console.error('Error loading license info:', e);
                        this.gritterErr('제품 정보 조회 중 오류가 발생했습니다.');
                    } finally {
                        this.state.loadingProducts = false;
                    }
                }
            );
        },
        gritterErr(msg) {
            const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.';
            this.swalUtils?.gritter
                ? this.swalUtils.gritter('error', m, 'error', 2000)
                : console.error('[ERR]', m);
        },
    },
};
</script>

<style scoped>
/* 외부 CSS 파일 사용: /ssflex/assets/css/crew-license-manager.css   앞으로 추가할 css 있으면 해당 파일에 추가*/
</style>

